import { createReducer } from './utils';
import {
  GET_ACTIVITIES_REQUEST,
  GET_ACTIVITIES_SUCCESS,
  GET_ACTIVITIES_FAILURE,
  CREATE_ACTIVITY_REQUEST,
  CREATE_ACTIVITY_SUCCESS,
  CREATE_ACTIVITY_FAILURE,
  UPDATE_ACTIVITY_FAILURE,
  UPDATE_ACTIVITY_REQUEST,
  UPDATE_ACTIVITY_SUCCESS,
  DELETE_ACTIVITY_REQUEST,
  DELETE_ACTIVITY_SUCCESS,
  DELETE_ACTIVITY_FAILURE,
  RESET_ACTIVITIES_STATE,
  GET_ACTIVITIES_BY_PLAN_ID_REQUEST,
  GET_ACTIVITIES_BY_PLAN_ID_SUCCESS,
  GET_ACTIVITIES_BY_PLAN_ID_FAILURE,
  SNACK_SET,
} from './constants';
import { Activity, ActivityBatchSaveInput, CreateActivityInput } from '@shared/schema';
import { ActivitiesState, Action, ThunkAPI } from '../types/state';
import { translate as t } from '@/core/i18n/translate';

const defaultState: ActivitiesState = {
  activities: [],
  loading: false,
  errorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [GET_ACTIVITIES_REQUEST]: handleGetActivitiesRequest,
  [GET_ACTIVITIES_SUCCESS]: handleGetActivitiesSuccess,
  [GET_ACTIVITIES_FAILURE]: handleGetActivitiesFailure,
  [CREATE_ACTIVITY_REQUEST]: handleCreateActivityRequest,
  [CREATE_ACTIVITY_SUCCESS]: handleCreateActivitySuccess,
  [CREATE_ACTIVITY_FAILURE]: handleCreateActivityFailure,
  [UPDATE_ACTIVITY_REQUEST]: handleUpdateActivityRequest,
  [UPDATE_ACTIVITY_SUCCESS]: handleUpdateActivitySuccess,
  [UPDATE_ACTIVITY_FAILURE]: handleUpdateActivityFailure,
  [DELETE_ACTIVITY_REQUEST]: handleDeleteActivityRequest,
  [DELETE_ACTIVITY_SUCCESS]: handleDeleteActivitySuccess,
  [DELETE_ACTIVITY_FAILURE]: handleDeleteActivityFailure,
  [GET_ACTIVITIES_BY_PLAN_ID_REQUEST]: handleGetActivitiesByPlanIdRequest,
  [GET_ACTIVITIES_BY_PLAN_ID_SUCCESS]: handleGetActivitiesByPlanIdSuccess,
  [GET_ACTIVITIES_BY_PLAN_ID_FAILURE]: handleGetActivitiesByPlanIdFailure,
  [RESET_ACTIVITIES_STATE]: () => defaultState,
});

function handleGetActivitiesByPlanIdRequest(state: ActivitiesState): ActivitiesState {
  return {
    ...state,
    loading: true,
  };
}

function handleGetActivitiesByPlanIdSuccess(
  state: ActivitiesState,
  _action: Action<unknown>,
): ActivitiesState {
  return {
    ...state,
    loading: false,
    errorMessage: null,
  };
}

function handleGetActivitiesByPlanIdFailure(
  state: ActivitiesState,
  _action: Action<unknown>,
): ActivitiesState {
  return {
    ...state,
    loading: false,
    errorMessage: t('apiFeedback.activities.fetchError'),
  };
}

function handleGetActivitiesRequest(state: ActivitiesState): ActivitiesState {
  return {
    ...state,
    loading: true,
  };
}

function handleGetActivitiesSuccess(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { activities: Activity[] } | undefined;
  return {
    ...state,
    activities: payload?.activities || [],
    loading: false,
    errorMessage: null,
  };
}

function handleGetActivitiesFailure(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || 'Error fetching activities',
  };
}

function handleCreateActivityRequest(state: ActivitiesState): ActivitiesState {
  return {
    ...state,
    loading: true,
  };
}

function handleCreateActivitySuccess(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { activity: Activity } | undefined;
  return {
    ...state,
    activities: payload?.activity ? [...state.activities, payload.activity] : state.activities,
    loading: false,
    errorMessage: null,
  };
}

function handleUpdateActivityRequest(state: ActivitiesState): ActivitiesState {
  return {
    ...state,
    loading: true,
  };
}

function handleUpdateActivitySuccess(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { activity: Activity } | undefined;
  return {
    ...state,
    activities: payload?.activity
      ? state.activities.map((act) =>
          act.APGroupActivityId === payload.activity.APGroupActivityId ? payload.activity : act,
        )
      : state.activities,
    loading: false,
    errorMessage: null,
  };
}

function handleUpdateActivityFailure(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || 'Error updating activity',
  };
}

function handleCreateActivityFailure(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || 'Error creating activity',
  };
}

function handleDeleteActivityRequest(state: ActivitiesState): ActivitiesState {
  return { ...state, loading: true };
}

function handleDeleteActivitySuccess(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { activityId: number } | undefined;
  return {
    ...state,
    activities: payload?.activityId
      ? state.activities.filter((a) => a.APGroupActivityId !== payload.activityId)
      : state.activities,
    loading: false,
    errorMessage: null,
  };
}

function handleDeleteActivityFailure(
  state: ActivitiesState,
  action: Action<unknown>,
): ActivitiesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || 'Error deleting activity',
  };
}

export function getActivitiesByObjectiveId(objectiveId: number, fulfillmentversion?: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_ACTIVITIES_REQUEST });
    try {
      const activities = await dataSource.getActivitiesByObjectiveId(
        objectiveId,
        fulfillmentversion,
      );
      dispatch({
        type: GET_ACTIVITIES_SUCCESS,
        payload: { activities },
      });
    } catch (error) {
      console.error('Error fetching activities:', error);
      dispatch({
        type: GET_ACTIVITIES_FAILURE,
        payload: { errorMessage: t('apiFeedback.activities.fetchError') },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.activities.fetchError'),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

export function fetchActivitiesByPlanId(
  _planId: number | null | undefined,
  planVersionId?: number | null,
  fulfillmentVersionId?: number | null,
) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ): Promise<Activity[]> => {
    if (!planVersionId) return [];
    try {
      dispatch({ type: GET_ACTIVITIES_BY_PLAN_ID_REQUEST });
      const activities = await dataSource.getActivitiesByPlanId(
        planVersionId,
        fulfillmentVersionId ?? undefined,
      );
      dispatch({
        type: GET_ACTIVITIES_SUCCESS,
        payload: { activities },
      });
      dispatch({ type: GET_ACTIVITIES_BY_PLAN_ID_SUCCESS });
      return activities;
    } catch (error) {
      dispatch({ type: GET_ACTIVITIES_BY_PLAN_ID_FAILURE });
      console.error('Error fetching activities by plan version:', error);
      return [];
    }
  };
}

export function createActivity(data: CreateActivityInput) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: CREATE_ACTIVITY_REQUEST });
    try {
      const activity = await dataSource.createActivity(data);
      dispatch({ type: CREATE_ACTIVITY_SUCCESS, payload: { activity } });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.createSuccess'),
            severity: 'success',
          },
        },
      });
      return activity;
    } catch (error) {
      console.error({ error });
      dispatch({
        type: CREATE_ACTIVITY_FAILURE,
        payload: { errorMessage: t('apiFeedback.activities.createError') },
      });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.createError'),
            severity: 'error',
          },
        },
      });
    }
    return undefined;
  };
}

export function saveActivitiesBatch(versionId: number, data: ActivityBatchSaveInput) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_ACTIVITIES_REQUEST });
    try {
      const result = await dataSource.saveActivitiesBatch(versionId, data);
      dispatch({ type: GET_ACTIVITIES_SUCCESS, payload: { activities: result.activities } });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.updateSuccess'),
            severity: 'success',
          },
        },
      });
      return result;
    } catch (error) {
      console.error('Error saving activities batch:', error);
      dispatch({
        type: GET_ACTIVITIES_FAILURE,
        payload: { errorMessage: t('apiFeedback.activities.updateError') },
      });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.updateError'),
            severity: 'error',
          },
        },
      });
      throw error;
    }
  };
}

export function updateActivityDefinition(activityId: number, data: Partial<CreateActivityInput>) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: UPDATE_ACTIVITY_REQUEST });
    try {
      const activity = await dataSource.updateActivityDefinition(activityId, data);
      dispatch({ type: UPDATE_ACTIVITY_SUCCESS, payload: { activity } });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.updateSuccess'),
            severity: 'success',
          },
        },
      });
      return activity;
    } catch (error) {
      console.error('Error updating activity definition:', error);
      dispatch({
        type: UPDATE_ACTIVITY_FAILURE,
        payload: { errorMessage: t('apiFeedback.activities.updateError') },
      });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.updateError'),
            severity: 'error',
          },
        },
      });
    }
    return undefined;
  };
}

export function deleteActivityDefinition(activityId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: DELETE_ACTIVITY_REQUEST });
    try {
      await dataSource.deleteActivity(activityId);
      dispatch({ type: DELETE_ACTIVITY_SUCCESS, payload: { activityId } });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.deleteSuccess'),
            severity: 'success',
          },
        },
      });
      return true;
    } catch (error) {
      console.error('Error deleting activity:', error);
      dispatch({
        type: DELETE_ACTIVITY_FAILURE,
        payload: { errorMessage: t('apiFeedback.activities.deleteError') },
      });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.deleteError'),
            severity: 'error',
          },
        },
      });
      return false;
    }
  };
}

export function updateActivityProgress(
  data: { activityId: number; progress: number }[],
  fulfillmentId: number,
) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: UPDATE_ACTIVITY_REQUEST });
    try {
      const updatedActivity = await dataSource.updateActivityProgress(data, fulfillmentId);
      dispatch({ type: UPDATE_ACTIVITY_SUCCESS, payload: { activity: updatedActivity } });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.updateSuccess'),
            severity: 'success',
          },
        },
      });
      return updatedActivity;
    } catch (error) {
      console.error({ error });
      dispatch({
        type: UPDATE_ACTIVITY_FAILURE,
        payload: { errorMessage: t('apiFeedback.activities.updateError') },
      });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.updateError'),
            severity: 'error',
          },
        },
      });
    }
  };
}

export function uploadActivityEvidenceAction(
  activityId: number,
  files: File[],
  fulfilmentId: number,
) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      const names = await dataSource.uploadActivityEvidence?.(activityId, files, fulfilmentId);
      return names || [];
    } catch (error) {
      console.error('Error uploading evidence', error);
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.evidenceUploadError'),
            severity: 'error',
          },
        },
      });
      return [];
    }
  };
}

export function deleteActivityEvidenceAction(activityId: number, fulfillmentVersion: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      await dataSource.deleteActivityEvidence?.(activityId, fulfillmentVersion);
    } catch (error) {
      console.error('Error deleting evidence', error);
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.evidenceDeleteError'),
            severity: 'error',
          },
        },
      });
    }
  };
}

export function getActivityEvidenceAction(activityId: number, fulfillmentId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      const files = await dataSource.getActivityEvidence?.(activityId, fulfillmentId);
      return files || [];
    } catch (error) {
      console.error('Error fetching evidence list', error);
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.evidenceFetchError'),
            severity: 'error',
          },
        },
      });
      return [];
    }
  };
}

export function deleteActivityEvidenceFileAction(
  activityId: number,
  fileName: string,
  fulfillmentVersionId: number,
) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      await dataSource.deleteActivityEvidenceFile?.(activityId, fileName, fulfillmentVersionId);
      return true;
    } catch (error) {
      console.error('Error deleting evidence file', error);
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.activities.evidenceFileDeleteError'),
            severity: 'error',
          },
        },
      });
      return false;
    }
  };
}

export function resetActivitiesState() {
  return (dispatch: ThunkAPI['dispatch']) => {
    dispatch({ type: 'RESET_ACTIVITIES_STATE' });
  };
}
